import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Badge } from '../../../components/ui/badge';
import { Switch } from '../../../components/ui/switch';
import { Checkbox } from '../../../components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '../../../components/ui/radio-group';
import { ScrollArea } from '../../../components/ui/scroll-area';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../../components/ui/alert-dialog';
import { Search, Loader2, Trash2, Layers, ListChecks, Truck, Bell, CalendarDays, CalendarRange } from 'lucide-react';
import { adminService } from '../../../services/adminService';
import { toast } from 'sonner';
import OrderImpactAlert from './OrderImpactAlert';
import RecipientBreakdown from './RecipientBreakdown';

const WEEKDAY_OPTS = [
  { key: 'monday', label: 'Mon' },
  { key: 'tuesday', label: 'Tue' },
  { key: 'wednesday', label: 'Wed' },
  { key: 'thursday', label: 'Thu' },
  { key: 'friday', label: 'Fri' },
  { key: 'saturday', label: 'Sat' },
  { key: 'sunday', label: 'Sun' },
];

// Parse a YYYY-MM-DD string to a friendly long label (timezone-safe).
const prettyDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(`${dateStr}T12:00:00`);
  return d.toLocaleDateString('en-US', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
  });
};

const MenuDayDialog = ({ open, onClose, date, existingMenu, products, onSaved }) => {
  const [menuDate, setMenuDate] = useState(date || '');
  const [title, setTitle] = useState('');
  const [mode, setMode] = useState('additive');
  const [published, setPublished] = useState(false);
  const [deliveryOverride, setDeliveryOverride] = useState(false);
  const [notes, setNotes] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [notifyOnSave, setNotifyOnSave] = useState(false);
  const [confirmNotify, setConfirmNotify] = useState(false);
  const [recipientCount, setRecipientCount] = useState(null);
  const [notifyDate, setNotifyDate] = useState(null);
  const [pendingBatchId, setPendingBatchId] = useState(null);
  const [rangeMode, setRangeMode] = useState(false);
  const [endDate, setEndDate] = useState('');
  const [rangeWeekdays, setRangeWeekdays] = useState([]);
  const [rangeOverwrite, setRangeOverwrite] = useState(false);
  const [rangePreview, setRangePreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [impact, setImpact] = useState(null);
  const [impactOpen, setImpactOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);

  useEffect(() => {
    if (!open) return;
    setMenuDate(date || '');
    if (existingMenu) {
      setTitle(existingMenu.title || '');
      setMode(existingMenu.mode || 'additive');
      setPublished(!!existingMenu.published);
      setDeliveryOverride(!!existingMenu.delivery_override);
      setNotes(existingMenu.notes || '');
      setSelectedIds((existingMenu.items || []).map((it) => it.product_id));
    } else {
      setTitle('');
      setMode('additive');
      setPublished(false);
      setDeliveryOverride(false);
      setNotes('');
      setSelectedIds([]);
    }
    setNotifyOnSave(false);
    setPendingBatchId(null);
    setRangeMode(false);
    setEndDate('');
    setRangeWeekdays([]);
    setRangeOverwrite(false);
    setRangePreview(null);
    setSearch('');
  }, [open, existingMenu, date]);

  const activeProducts = useMemo(
    () => products.filter((p) => p.status === 'available' || p.status === 'active'),
    [products]
  );

  const filteredProducts = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return activeProducts;
    return activeProducts.filter((p) => p.name.toLowerCase().includes(q));
  }, [activeProducts, search]);

  // Conflicts = existing menus on other dates (the origin day is always absorbed).
  const conflictDates = useMemo(
    () => (rangePreview?.overwrite_dates || []).filter((d) => d !== menuDate),
    [rangePreview, menuDate]
  );

  const toggleProduct = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleWeekday = (key) =>
    setRangeWeekdays((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));

  const validateForPublish = () => {
    if (published && selectedIds.length === 0) {
      toast.error('Add at least one product before publishing a menu.');
      return false;
    }
    if (published && mode === 'exclusive' && selectedIds.length === 0) {
      toast.error('An exclusive menu must list at least one product.');
      return false;
    }
    return true;
  };

  const handlePreviewRange = async () => {
    if (!endDate) { toast.error('Pick an end date'); return; }
    if (endDate < menuDate) { toast.error('End date must be on or after the start date'); return; }
    setPreviewing(true);
    try {
      const data = await adminService.previewMenuRange({
        start_date: menuDate, end_date: endDate, weekday_mask: rangeWeekdays,
      });
      setRangePreview(data);
      if (data.target_dates.length === 0) toast.warning('No dates in that range match the weekday filter');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Preview failed');
    } finally {
      setPreviewing(false);
    }
  };

  const openNotifyConfirm = async ({ batchId, dayDate }) => {
    try {
      const { recipient_count } = await adminService.getMenuRecipientsCount();
      setRecipientCount(recipient_count);
    } catch {
      setRecipientCount(null);
    }
    setPendingBatchId(batchId);
    setNotifyDate(dayDate);
    setConfirmNotify(true);
    setSaving(false);
  };

  const buildFormPayload = () => ({
    title: title.trim(),
    mode,
    published,
    delivery_override: deliveryOverride,
    notes,
    items: selectedIds.map((pid) => ({ product_id: pid })),
  });

  // The proposed END-state of a date, for the order-impact guard.
  const buildProposedMenu = () => ({
    mode,
    published,
    delivery_override: deliveryOverride,
    items: selectedIds.map((pid) => ({ product_id: pid })),
  });

  // Order-impact guard: warn before saving if placed orders would be stranded.
  // Fails open — a guard error never blocks the admin from saving.
  const checkImpactThen = async (changes, action) => {
    setSaving(true);
    try {
      const result = await adminService.previewMenuOrderImpact({ changes });
      if (result.total_orders_affected > 0) {
        setImpact(result);
        setPendingAction(() => action);
        setImpactOpen(true);
        setSaving(false);
        return;
      }
    } catch {
      // fail open
    }
    await action();
  };

  const doExtend = async () => {
    if (!endDate) { toast.error('Pick an end date for the range'); return; }
    if (endDate < menuDate) { toast.error('End date must be on or after the start date'); return; }
    setSaving(true);
    try {
      const result = await adminService.extendMenuDay(date, {
        end_date: endDate,
        weekday_mask: rangeWeekdays,
        title: title.trim(),
        mode,
        items: selectedIds.map((pid) => ({ product_id: pid })),
        delivery_override: deliveryOverride,
        published,
        overwrite: rangeOverwrite,
      });
      const parts = [];
      if (result.absorbed_count) parts.push(`${result.absorbed_count} kept`);
      if (result.created_count) parts.push(`${result.created_count} added`);
      if (result.overwritten_count) parts.push(`${result.overwritten_count} overwritten`);
      if (result.skipped_count) parts.push(`${result.skipped_count} skipped`);
      toast.success(`Menu extended to a range — ${parts.join(', ')}`);
      onSaved();
      if (notifyOnSave && published) {
        await openNotifyConfirm({ batchId: result.batch.id, dayDate: null });
        return;
      }
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to extend menu');
    } finally {
      setSaving(false);
    }
  };

  const doSaveDay = async () => {
    setSaving(true);
    try {
      if (existingMenu) {
        await adminService.updateMenuDay(date, { ...buildFormPayload(), new_date: menuDate });
        toast.success('Menu updated');
      } else {
        await adminService.createMenuDay({ date: menuDate, ...buildFormPayload() });
        toast.success('Menu created');
      }
      onSaved();
      // Offer to (re)notify clients if requested and the menu is live.
      if (notifyOnSave && published) {
        await openNotifyConfirm({ batchId: null, dayDate: menuDate });
        return;
      }
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save menu');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    if (!menuDate) {
      toast.error('Pick a date for this menu.');
      return;
    }
    if (!validateForPublish()) return;
    if (rangeMode) {
      if (!endDate) { toast.error('Pick an end date for the range'); return; }
      if (endDate < menuDate) { toast.error('End date must be on or after the start date'); return; }
      const rangeDates = (rangePreview?.target_dates || []).filter(
        (d) => !conflictDates.includes(d) || rangeOverwrite
      );
      return checkImpactThen(
        rangeDates.map((d) => ({ date: d, menu: buildProposedMenu() })),
        doExtend
      );
    }
    const changes = existingMenu && menuDate !== date
      ? [{ date, menu: null }, { date: menuDate, menu: buildProposedMenu() }]
      : [{ date: menuDate, menu: buildProposedMenu() }];
    return checkImpactThen(changes, doSaveDay);
  };

  const handleNotify = async () => {
    setSaving(true);
    try {
      const res = pendingBatchId
        ? await adminService.notifyMenuBatch(pendingBatchId, true)
        : await adminService.notifyMenuDay(notifyDate, true);
      toast.success(`Announcement sent — ${res.emails_sent} email(s)`);
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to send announcement');
    } finally {
      setSaving(false);
      setConfirmNotify(false);
    }
  };

  const doDelete = async () => {
    setSaving(true);
    try {
      await adminService.deleteMenuDay(date);
      toast.success('Menu deleted');
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to delete menu');
    } finally {
      setSaving(false);
      setConfirmDelete(false);
    }
  };

  const handleDelete = () => {
    setConfirmDelete(false);
    checkImpactThen([{ date, menu: null }], doDelete);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="menu-day-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {existingMenu ? 'Edit Menu' : 'Schedule Menu'}
            {published ? (
              <Badge className="bg-green-100 text-green-800">Published</Badge>
            ) : (
              <Badge variant="outline">Draft</Badge>
            )}
          </DialogTitle>
          <DialogDescription>{prettyDate(menuDate)}</DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          {/* Date */}
          <div>
            <Label htmlFor="menu-date" className="flex items-center gap-2">
              <CalendarDays className="h-4 w-4 text-amber-600" /> Menu date
            </Label>
            <Input
              id="menu-date"
              type="date"
              value={menuDate}
              disabled={rangeMode}
              onChange={(e) => setMenuDate(e.target.value)}
              className="mt-1"
              data-testid="menu-date-input"
            />
            {existingMenu && !rangeMode && menuDate !== date && (
              <p className="text-xs text-amber-600 mt-1" data-testid="menu-date-move-hint">
                This menu will be moved from {date} to {menuDate}.
              </p>
            )}
          </div>

          {/* Extend to a date range (existing single-day menus only) */}
          {existingMenu && !existingMenu.batch_id && (
            <div className="rounded-lg border p-3 space-y-3">
              <div className="flex items-center justify-between">
                <div className="pr-3">
                  <p className="text-sm font-medium flex items-center gap-2">
                    <CalendarRange className="h-4 w-4 text-amber-600" /> Apply to a date range
                  </p>
                  <p className="text-xs text-gray-500">
                    Extend this menu across multiple days — creates a range batch you can manage together.
                  </p>
                </div>
                <Switch
                  checked={rangeMode}
                  onCheckedChange={(v) => { setRangeMode(v); if (v) setMenuDate(date); setRangePreview(null); }}
                  data-testid="menu-range-mode-switch"
                />
              </div>

              {rangeMode && (
                <div className="space-y-3 pt-1">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Start</Label>
                      <Input type="date" value={menuDate} disabled className="mt-1" data-testid="menu-range-start-date" />
                    </div>
                    <div>
                      <Label className="text-xs">End</Label>
                      <Input
                        type="date"
                        value={endDate}
                        min={menuDate}
                        onChange={(e) => { setEndDate(e.target.value); setRangePreview(null); }}
                        className="mt-1"
                        data-testid="menu-range-end-date"
                      />
                    </div>
                  </div>

                  <div>
                    <Label className="text-xs">Only these weekdays <span className="text-gray-400">(none = every day)</span></Label>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {WEEKDAY_OPTS.map((w) => (
                        <button
                          key={w.key}
                          type="button"
                          onClick={() => { toggleWeekday(w.key); setRangePreview(null); }}
                          data-testid={`menu-range-weekday-${w.key}`}
                          className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                            rangeWeekdays.includes(w.key)
                              ? 'bg-amber-500 text-white border-amber-500'
                              : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                          }`}
                        >
                          {w.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="rounded-lg border p-3 bg-gray-50">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium">Conflict preview</p>
                      <Button variant="outline" size="sm" onClick={handlePreviewRange} disabled={previewing} data-testid="menu-range-preview-btn">
                        {previewing ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Preview dates'}
                      </Button>
                    </div>
                    {rangePreview && (
                      <div className="mt-3 text-sm space-y-2" data-testid="menu-range-preview-result">
                        <div className="flex flex-wrap gap-2">
                          <Badge className="bg-blue-100 text-blue-800">{rangePreview.target_dates.length} dates</Badge>
                          <Badge className="bg-green-100 text-green-800">{rangePreview.new_dates.length} new</Badge>
                          {conflictDates.length > 0 && (
                            <Badge className="bg-orange-100 text-orange-800" data-testid="menu-range-conflict-count">
                              {conflictDates.length} conflict{conflictDates.length === 1 ? '' : 's'}
                            </Badge>
                          )}
                        </div>
                        {conflictDates.length > 0 && (
                          <label className="flex items-center gap-2 cursor-pointer pt-1">
                            <Checkbox checked={rangeOverwrite} onCheckedChange={(v) => setRangeOverwrite(!!v)} data-testid="menu-range-overwrite-check" />
                            <span className="text-xs text-gray-700">Overwrite the {conflictDates.length} date(s) already owned by another menu (otherwise they're skipped)</span>
                          </label>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Title */}
          <div>
            <Label htmlFor="menu-title">Menu title</Label>
            <Input
              id="menu-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Father's Day Specials"
              className="mt-1"
              data-testid="menu-title-input"
            />
          </div>

          {/* Mode */}
          <div>
            <Label className="flex items-center gap-2"><Layers className="h-4 w-4 text-amber-600" /> Menu mode</Label>
            <RadioGroup value={mode} onValueChange={setMode} className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <label className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer ${mode === 'additive' ? 'border-amber-500 bg-amber-50' : 'border-gray-200'}`}>
                <RadioGroupItem value="additive" id="mode-additive" data-testid="menu-mode-additive" className="mt-1" />
                <div>
                  <p className="text-sm font-medium">Additive</p>
                  <p className="text-xs text-gray-500">Adds these items on top of the normal day's catalog.</p>
                </div>
              </label>
              <label className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer ${mode === 'exclusive' ? 'border-amber-500 bg-amber-50' : 'border-gray-200'}`}>
                <RadioGroupItem value="exclusive" id="mode-exclusive" data-testid="menu-mode-exclusive" className="mt-1" />
                <div>
                  <p className="text-sm font-medium">Exclusive</p>
                  <p className="text-xs text-gray-500">Only these items are available — replaces the normal catalog.</p>
                </div>
              </label>
            </RadioGroup>
          </div>

          {/* Product picker */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="flex items-center gap-2"><ListChecks className="h-4 w-4 text-amber-600" /> Products</Label>
              <span className="text-xs text-gray-500" data-testid="menu-selected-count">{selectedIds.length} selected</span>
            </div>
            <div className="relative mb-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search products..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="menu-product-search"
              />
            </div>
            <ScrollArea className="h-56 rounded-lg border">
              <div className="p-2 space-y-1">
                {filteredProducts.length === 0 ? (
                  <p className="text-sm text-gray-400 p-3">No products found</p>
                ) : (
                  filteredProducts.map((p) => (
                    <label
                      key={p.id}
                      className="flex items-center gap-3 rounded-md px-2 py-1.5 hover:bg-gray-50 cursor-pointer"
                      data-testid={`menu-product-row-${p.id}`}
                    >
                      <Checkbox
                        checked={selectedIds.includes(p.id)}
                        onCheckedChange={() => toggleProduct(p.id)}
                        data-testid={`menu-product-check-${p.id}`}
                      />
                      {p.image_url && (
                        <img src={p.image_url} alt={p.name} className="w-8 h-8 rounded object-cover" />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{p.name}</p>
                        <p className="text-xs text-gray-400 uppercase">{p.category}</p>
                      </div>
                      <span className="text-xs text-gray-500">${(p.wholesale_price || 0).toFixed(2)}</span>
                    </label>
                  ))
                )}
              </div>
            </ScrollArea>
          </div>

          {/* Notes */}
          <div>
            <Label htmlFor="menu-notes">Notes (internal)</Label>
            <Textarea
              id="menu-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="mt-1"
              data-testid="menu-notes-input"
            />
          </div>

          {/* Published toggle */}
          <div className="flex items-center justify-between rounded-lg border p-3">
            <div>
              <p className="text-sm font-medium">Published</p>
              <p className="text-xs text-gray-500">Only published menus are visible to clients.</p>
            </div>
            <Switch
              checked={published}
              onCheckedChange={(v) => { setPublished(v); if (!v) setNotifyOnSave(false); }}
              data-testid="menu-published-switch"
            />
          </div>

          {/* Notify clients (only meaningful when published) */}
          {published && (
            <label className="flex items-center gap-2 cursor-pointer rounded-lg border border-amber-200 bg-amber-50 p-3" data-testid="menu-notify-label">
              <Checkbox checked={notifyOnSave} onCheckedChange={(v) => setNotifyOnSave(!!v)} data-testid="menu-notify-check" />
              <span className="flex items-center gap-1.5 text-sm text-amber-800">
                <Bell className="h-4 w-4" /> Notify clients of this menu on save (email with product images + their pricing)
              </span>
            </label>
          )}

          {/* Delivery override (tucked away) */}
          <div className="flex items-start justify-between rounded-lg border border-dashed p-3">
            <div className="flex items-start gap-2 pr-3">
              <Truck className="h-4 w-4 text-gray-400 mt-0.5" />
              <div>
                <p className="text-sm font-medium">Deliver to everyone this day</p>
                <p className="text-xs text-gray-500">
                  Override the weekly schedule and make this date orderable for all clients,
                  even if it isn't one of their normal delivery days.
                </p>
              </div>
            </div>
            <Switch checked={deliveryOverride} onCheckedChange={setDeliveryOverride} data-testid="menu-override-switch" />
          </div>
        </div>

        <DialogFooter className="mt-2 flex-col sm:flex-row gap-2">
          {existingMenu && (
            <Button
              variant="outline"
              className="text-red-600 hover:text-red-700 sm:mr-auto"
              onClick={() => setConfirmDelete(true)}
              disabled={saving}
              data-testid="menu-delete-btn"
            >
              <Trash2 className="h-4 w-4 mr-2" /> Delete
            </Button>
          )}
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button
            className="bg-amber-500 hover:bg-amber-600"
            onClick={handleSave}
            disabled={saving || (rangeMode && !rangePreview)}
            data-testid="menu-save-btn"
          >
            {saving
              ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving…</>
              : (rangeMode ? 'Save as Range' : 'Save Menu')}
          </Button>
        </DialogFooter>
      </DialogContent>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this menu?</AlertDialogTitle>
            <AlertDialogDescription>
              The menu for {prettyDate(date)} will be removed. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={handleDelete} data-testid="menu-confirm-delete-btn">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <OrderImpactAlert
        open={impactOpen}
        onOpenChange={(o) => { if (!o) { setImpactOpen(false); setPendingAction(null); } }}
        impact={impact}
        onProceed={() => { setImpactOpen(false); pendingAction?.(); }}
      />

      <AlertDialog open={confirmNotify} onOpenChange={(o) => { if (!o) { setConfirmNotify(false); onClose(); } }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Notify clients?</AlertDialogTitle>
            <AlertDialogDescription>
              This will email{' '}
              <RecipientBreakdown count={recipientCount} testId="menu-day-recipient-count" />{' '}
              that &ldquo;{title || 'this menu'}&rdquo; is live
              {pendingBatchId ? ' across the selected range' : ` for ${prettyDate(notifyDate)}`}.
              Clients, their team and their extra contacts get product images and that
              account&apos;s pricing; marketing recipients get images and descriptions with
              no pricing.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => { setConfirmNotify(false); onClose(); }}>Skip</AlertDialogCancel>
            <AlertDialogAction className="bg-amber-600 hover:bg-amber-700" onClick={handleNotify} data-testid="menu-day-confirm-notify-btn">
              Send Announcement
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
};

export default MenuDayDialog;
